import { mat3, mat4, quat, vec3 } from "gl-matrix";
import { AssetManager } from "./AssetManager";
import { Camera, ExportedCamera } from "./Camera";
import Light, { ExportedLight } from "./Lights/Light";
import { PointLight } from "./Lights/PointLight";
import { Cube } from "./Objects/Cube";
import { Mesh } from "./Objects/Mesh";
import { Plane } from "./Objects/Plane";
import { RenderObject, RenderObjectConstructorArgs, RenderObjectExportValues } from "./Objects/RenderObject";
import { Sphere } from "./Objects/Sphere";
import { Material, ObjectType, ObjectTypeToString } from "./Objects/types";
import { Renderer } from "./Renderer";
import { getShaderTypeFromExported, ShaderManager } from "./ShaderManager";
import sdata from "./Shaders/sdata.json";
import { TextureManager } from "./TextureManager";
import { MeshData, MeshManager } from "./MeshManager";
import { SceneSettingsData } from "../Components/Modals/SceneSettingsModal";


export type Picker = {
  mouseX: number;
  mouseY: number;
  active: boolean;
  targetTexture: WebGLTexture | null;
  depthBuffer: WebGLBuffer | null;
  frameBuffer: WebGLBuffer | null;
};

export type SceneState = {
  objects: RenderObjectExportValues[];
  pointLights: ExportedLight[];
  numberOfObjectsToLoad?: number;
  skyBoxOn?: boolean;
  skyBoxPath?: string;
  sceneSettings?: {
    backgroundColor: vec3,
  };
  camera: ExportedCamera;
};

export class SceneManager {
  public objects: { [key: string]: RenderObject };
  public lights: { [key: string]: Light };
  public camera: Camera;
  public selected: RenderObject | Light | undefined;
  public sceneName: string;

  private _textureManager: TextureManager;
  private _shaderManager: ShaderManager;
  private _assetManager: AssetManager;
  private _meshManager: MeshManager;
  private _loadedObjects: number;
  private _gl: WebGL2RenderingContext;
  private _renderer: Renderer;
  private _picker: Picker = {
    mouseX: 0,
    mouseY: 0,
    active: false,
    targetTexture: null,
    depthBuffer: null,
    frameBuffer: null,
  };

  // TODO - skybox stuff, for now always off
  private _skyboxEnabled: boolean = false;
  private _skyboxPath: string = "defaultSkybox";

  _onRender: (render: boolean) => void;

  constructor(gl: WebGL2RenderingContext, canvas: HTMLCanvasElement, onRender: (render: boolean) => void) {
    this.objects = {};
    this.lights = {};
    this.camera = new Camera(onRender, { position: vec3.fromValues(0, 2, 5) });
    this.camera.lookAt(vec3.fromValues(0, 0, 0));

    this._textureManager = new TextureManager();
    this._shaderManager = new ShaderManager(sdata);
    this._assetManager = new AssetManager();
    this._meshManager = new MeshManager();
    this._shaderManager.compileAll(gl);
    this._loadedObjects = 0;
    this._gl = gl;
    this._renderer = new Renderer({ canvas });
    this.initPickerBuffers(gl);
    this._onRender = onRender;
    this.sceneName = "";
  }

  async init() {
    // for loading all things that need to be loaded async
    await this._textureManager.init(this._gl);
    await this._meshManager.init();
  }

  refresh() {
    this._onRender(true);
  }

  getGLContext(): WebGL2RenderingContext {
    return this._gl;
  }

  getRenderer(): Renderer {
    return this._renderer;
  }

  getSceneSettingsData(): SceneSettingsData {
    return {
      backgroundColour: this._renderer.getBackgroundColour(),
    }
  }

  async addObject(object: RenderObject) {
    // check if it exists already by name
    if (object.name in this.objects) {
      throw new Error("duplicate object name");
    }
    // probably need to init & setup the object first
    object.setup(this._gl, this._shaderManager, this._textureManager);
    this.objects[object.name] = object;
    this._loadedObjects++;
    this.refresh();
  }

  cloneObject(object: RenderObject | Light) {
    if (object instanceof RenderObject) {
      // add -copy until the name isn't taken
      let name = object.name;
      while (this.objects[name]) {
        name += "-copy";
      }
      const copy = object.clone(name);
      this.addObject(copy);
    } else {
      let name = object.name;
      while (this.lights[name]) {
        name += "-copy";
      }
      // just add a light with the same settings as the other light
      const copy = new Light({
        name,
        strength: object.strength,
        position: object.position,
        parent: object.parent,
        colour: [object.colour[0], object.colour[1], object.colour[2]],
        quadratic: object.quadratic,
        linear: object.linear,
        constant: object.constant,
      });
      this.addLight(copy);
    }
  }

  deleteObject(object: RenderObject | Light) {
    if (object instanceof RenderObject) {
      delete this.objects[object.name];
      object.delete(this._gl);
      this._loadedObjects--;
    } else {
      // lights are simple, just delete it from our light map
      delete this.lights[object.name];
    }
    this.setSelected(undefined);
  }

  getObject(name: string): RenderObject | undefined {
    if (name in this.objects) {
      return this.objects[name];
    }
    return undefined;
  }

  setSelected(object: RenderObject | Light | undefined) {
    this.selected = object;
    this.refresh();
  }

  resetSelected() {
    if (!this.selected) {
      return;
    }
    if (this.selected instanceof RenderObject) {
      this.selected.setup(this._gl, this._shaderManager, this._textureManager);
    }
  }

  addLight(light: Light) {
    if (light.name in this.lights) {
      throw new Error("duplicate light name");
    }
    this.lights[light.name] = light;
    this.refresh();
  }

  getTextureManager(): TextureManager {
    return this._textureManager;
  }

  getAssetManager(): AssetManager {
    return this._assetManager;
  }

  getMeshManager(): MeshManager {
    return this._meshManager;
  }

  initPickerBuffers(gl: WebGL2RenderingContext) {
    this._picker.frameBuffer = gl.createFramebuffer();
    this._picker.depthBuffer = gl.createRenderbuffer();
    this._picker.targetTexture = gl.createTexture();
  }

  getPicker(): Picker {
    return this._picker;
  }

  isLoaded(): boolean {
    return this._loadedObjects === Object.keys(this.objects).length;
  }

  render(deltaTime: number) {
    // regular render loop for objects
    this._renderer.drawPicker(this._gl, this._shaderManager, this._picker, this.objects, this.camera);
    this.checkPickerSelection((selected) => {
      // TODO - maybe do something additional with this selected object?
    });
    this._renderer.drawRegular(this._gl, this.objects, this.lights, this.camera);
    this._renderer.drawStencil(this._gl, this._shaderManager, this.camera, this.selected);
    this.camera.update(deltaTime);

    // check if we can stop rendering
    if (this._loadedObjects === Object.keys(this.objects).length && !this.camera.move) {
      this._onRender(false);
    }
  }

  // helper function that exports the current scene's data
  export(): SceneState {
    return {
      objects: Object.values(this.objects).map(ro => ro.export()),
      pointLights: Object.values(this.lights).map(l => l.exportValues()),
      skyBoxOn: this._skyboxEnabled,
      skyBoxPath: this._skyboxPath,
      sceneSettings: {
        backgroundColor: this._renderer.getBackgroundColour(),
      },
      camera: this.camera.export(),
    }
  }

  async import(importState: SceneState): Promise<Error | undefined> {
    try {
      // TODO - cleanup any current state objects (buffers, textures etc, for now YOLO)
      this.objects = {};
      this._loadedObjects = 0;

      const children: RenderObjectExportValues[] = [];

      importState.objects.forEach(async (v) => {
        if (v.parent) {
          children.push(v);
        }
        const rotMat = v.rotation as mat4;
        const rot3x3 = mat3.create();
        mat3.fromMat4(rot3x3, rotMat);
        const q = quat.create();
        quat.fromMat3(q, rot3x3);

        const objectArgs: RenderObjectConstructorArgs = {
          name: v.name,
          position: vec3.fromValues(v.position[0], v.position[1], v.position[2]),
          scale: vec3.fromValues(v.scale[0], v.scale[1], v.scale[2]),
          rotation: q,
          rotationEulers: v.rotationEulers,
        };

        // material requires some manual type fixing
        const material: Material = {
          ...v.material,
          shaderType: getShaderTypeFromExported(v.material.shaderType),
          ambient: vec3.fromValues(v.material.ambient[0], v.material.ambient[1], v.material.ambient[2]),
          diffuse: vec3.fromValues(v.material.diffuse[0], v.material.diffuse[1], v.material.diffuse[2]),
          specular: vec3.fromValues(v.material.specular[0], v.material.specular[1], v.material.specular[2]),
        };
        material.diffuse = vec3.fromValues(v.material.diffuse[0], v.material.diffuse[1], v.material.diffuse[2]);
        material.ambient = vec3.fromValues(v.material.ambient[0], v.material.ambient[1], v.material.ambient[2]);
        material.specular = vec3.fromValues(v.material.specular[0], v.material.specular[1], v.material.specular[2]);

        if (v.type === ObjectTypeToString(ObjectType.CUBE)) {
          this.addObject(new Cube({ ...objectArgs, material, }));
        } else if (v.type === ObjectTypeToString(ObjectType.MESH) && v.fileName) {
          const meshData: MeshData = this._meshManager.getMeshData(v.fileName);
          const mesh = new Mesh(
            { ...objectArgs, fileName: v.fileName, material, objData: meshData })

          this.addObject(mesh);
        } else if (v.type === ObjectTypeToString(ObjectType.PLANE)) {
          this.addObject(new Plane({ ...objectArgs, material }));
        } else if (v.type === ObjectTypeToString(ObjectType.SPHERE)) {
          this.addObject(new Sphere({ ...objectArgs, material }));
        }
      });

      // loop again and add the parent relationships
      children.forEach(async (v) => {
        if (v.parent) {
          const parentObject = this.getObject(v.parent);
          const child = this.getObject(v.name);

          if (child && parentObject)
            child.parent = parentObject;
        }
      });

      this.lights = {};
      importState.pointLights.forEach(pl => {
        this.addLight(new PointLight({
          ...pl,
          name: pl.name,
          position: pl.position as vec3,
          colour: [pl.colour[0], pl.colour[1], pl.colour[2]],
        }));
      });

      // import the camera
      const camera = importState.camera;
      if (camera) {
        const viewMatrix = camera.viewMatrix;
        const rotation = Camera.getQuatFromViewMatrix(viewMatrix);
        this.camera.name = camera.name;
        this.camera.position = vec3.fromValues(camera.position[0], camera.position[1], camera.position[2]);
        this.camera.rotation = quat.fromValues(rotation[0], rotation[1], rotation[2], rotation[3]);
        this.camera.moveSpeed = camera.moveSpeed;
        this.camera.sensitivity = camera.sensitivity;
      }

      if (importState.sceneSettings?.backgroundColor) {
        this._renderer.setBackgroundColour(importState.sceneSettings.backgroundColor);
      }

    } catch (err) {
      return err as Error;
    }

    return undefined;
  }

  checkPickerSelection(handleSelect: (selected?: RenderObject) => void) {
    function nearlyEqual(a: number, b: number, eps = 1e-3): boolean {
      return Math.abs(a - b) < eps;
    }
    if (!this._picker.active) {
      return;
    }

    const canvas = this._renderer.getCanvas();
    if (!canvas) {
      console.error("cannot check picker, no valid canvas set");
      return;
    }
    let gl = this._gl;
    this._picker.active = false;
    const pixelX =
      (this._picker.mouseX * canvas.width) / canvas.clientWidth;
    const pixelY =
      canvas.height -
      (this._picker.mouseY * canvas.height) / canvas.clientHeight -
      1;
    const data = new Float32Array(4);
    gl.readPixels(
      pixelX, // x
      pixelY, // y
      1, // width
      1, // height
      gl.RGBA, // format
      gl.FLOAT, // type
      data,
    ); // typed array to hold result
    let found = false;
    let objectArray = Object.values(this.objects);

    for (let i = 0; i < objectArray.length; i++) {
      const object = objectArray[i];
      if (!object.id) continue;

      if (
        nearlyEqual(object.id[0], data[0]) &&
        nearlyEqual(object.id[1], data[1]) &&
        nearlyEqual(object.id[2], data[2])
      ) {
        this.selected = object;
        handleSelect(object);
        found = true;
        break;
      }
    }

    if (!found) {
      this.selected = undefined;
      handleSelect(undefined);
    }
  }
}
